import { useMemo } from 'react'
import type { PageViewMode } from '@/components/pages/home'
import type { SpreadBackgroundGeometry } from '@/components/app/backgrounds/spread-background-layer'
import type { GridSnapLiveRect } from '@/lib/guides/grid-snap'
import { EDITOR_PASTEBOARD_SIZE_PT } from '@/lib/photos/photo-placement-config'
import {
  computeFitContentDimensions,
  computeGridSnapLiveRects,
  computeSinglePageHorizontalGuideSpan,
  resolveSinglePageSide,
} from '../helpers/spread-geometry'

/**
 * The spread's derived layout in canvas space: single-page vs spread, page and fit sizes, the
 * ruler zero point / bindery seam, the pasteboard's on-screen size, the background geometry, the
 * snap-to-grid live rects and step, and the horizontal-guide clip span.
 */
export function useSpreadLayout({
  pageViewMode,
  gridMode,
  scale,
  bleedWidth,
  bleedHeight,
  trimWidth,
  trimHeight,
  bleedMargin,
}: {
  pageViewMode: PageViewMode
  gridMode: 'off' | 'full' | 'half'
  scale: number
  bleedWidth: number
  bleedHeight: number
  trimWidth: number
  trimHeight: number
  bleedMargin: number
}) {
  /** Same step as the painted overlay: half = 6pt, full or off = 12pt (must stay in sync with `gridMode`). */
  const documentGridSnapStepPt = gridMode === 'half' ? 6 : 12

  const isSinglePage = pageViewMode === 'single-first' || pageViewMode === 'single-last'

  const pageTrimWidth = useMemo(() => Math.max(0, Math.floor(trimWidth / 2)), [trimWidth])
  /** First/last page: trim + outer bleed on spine and fore edge (Figma Layout=Start/End). */
  const singlePageBleedWidth = useMemo(
    () => pageTrimWidth + 2 * bleedMargin,
    [pageTrimWidth, bleedMargin]
  )

  const fitContentDimensions = useMemo(
    () =>
      computeFitContentDimensions({
        isSinglePage,
        bleedWidth,
        bleedHeight,
        singlePageBleedWidth,
      }),
    [isSinglePage, bleedWidth, bleedHeight, singlePageBleedWidth]
  )
  const horizontalRulerZeroAtCanvasX = bleedMargin + pageTrimWidth
  const verticalRulerZeroAtCanvasY = bleedMargin

  /**
   * On-screen pixel size of the pasteboard. Scales with zoom so the pasteboard's
   * document-space extent (`EDITOR_PASTEBOARD_SIZE_PT`) stays constant across zoom levels —
   * a fixed CSS-pixel size here would make the reachable document area grow as you zoom out
   * and shrink as you zoom in, instead of always reaching the same point on the ruler.
   */
  const pasteboardSize = EDITOR_PASTEBOARD_SIZE_PT * scale

  /**
   * Bindery seam X — shared by ruler 0, the spine bar, the spine grid column, page-trim split,
   * dashed trim, gutter tint, and margin guides (all consume this one value so they can never
   * drift apart from each other). Deliberately NOT snapped to the device-pixel grid: snapping is
   * zoom-dependent (it rounds to whatever the *current* zoom's nearest pixel is), so a value used
   * for real page geometry — not just a cosmetic line's own crispness — would visibly shift the
   * page split by a fraction of a point on every zoom change, which reads as the spine "moving"
   * even though nothing about the document changed. `horizontalRulerZeroAtCanvasX` is a pure
   * function of `trimWidth` alone, so it's already zoom-independent and stable by construction —
   * that stability matters more here than the sub-pixel antialiasing a snap would buy.
   */
  const snappedSpreadOriginCanvasX = horizontalRulerZeroAtCanvasX

  /** Shared by the background drop-target layer and the fills rendered inside the document. */
  const spreadBackgroundGeometry = useMemo<SpreadBackgroundGeometry>(
    () => ({
      bleedWidth,
      pageTrimWidth,
      trimHeight,
      bleedMargin,
      spreadOriginCanvasX: snappedSpreadOriginCanvasX,
      singlePageSide: resolveSinglePageSide(pageViewMode),
    }),
    [bleedWidth, pageTrimWidth, trimHeight, bleedMargin, snappedSpreadOriginCanvasX, pageViewMode]
  )

  const gridSnapLiveRects = useMemo<GridSnapLiveRect[]>(
    () =>
      computeGridSnapLiveRects({
        pageViewMode,
        pageTrimWidth,
        bleedMargin,
        bleedHeight,
        snappedSpreadOriginCanvasX,
        singlePageBleedWidth,
      }),
    [
      pageTrimWidth,
      bleedMargin,
      bleedHeight,
      pageViewMode,
      snappedSpreadOriginCanvasX,
      singlePageBleedWidth,
    ]
  )

  const singlePageHorizontalGuideSpan = useMemo(
    () =>
      computeSinglePageHorizontalGuideSpan({
        isSinglePage,
        pageViewMode,
        bleedMargin,
        bleedWidth,
        pageTrimWidth,
        snappedSpreadOriginCanvasX,
        singlePageBleedWidth,
      }),
    [
      isSinglePage,
      pageViewMode,
      bleedMargin,
      bleedWidth,
      pageTrimWidth,
      snappedSpreadOriginCanvasX,
      singlePageBleedWidth,
    ]
  )

  return {
    documentGridSnapStepPt,
    isSinglePage,
    pageTrimWidth,
    singlePageBleedWidth,
    fitContentDimensions,
    verticalRulerZeroAtCanvasY,
    pasteboardSize,
    snappedSpreadOriginCanvasX,
    spreadBackgroundGeometry,
    gridSnapLiveRects,
    singlePageHorizontalGuideSpan,
  }
}
